import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

// Types (Reusing similar structure but adapted for Prudent Investor)
interface TechnicalIndicators {
    rsi_14?: number;
    stoch_k?: number;
    stoch_d?: number;
    variation_5d?: number;
    volume_ratio?: number;
}

interface SwingOpportunity {
    symbole: string;
    nom_societe: string;
    secteur?: string;
    cours_actuel: number;
    score: number;
    signal_type: string;
    entry_price: number;
    target_price: number;
    stop_loss: number;
    position_size: number;
    position_value: number;
    stoch_k?: number;
    stoch_d?: number;
    rsi?: number;
    avg_volume?: number;
    justification: string;
}

interface SellSignal {
    symbole: string;
    nom_societe: string;
    quantity_held: number;
    current_price: number;
    unrealized_pnl_pct: number;
    signal_strength: string;
    signal_type: string;
    recommended_action: string;
    justification: string;
}

interface PortfolioSummary {
    cash_available: number;
    cash_blocked_j2: number;
    total_portfolio_value: number;
    open_swing_positions: number;
    max_position_value: number;
    risk_per_trade: number;
}

interface EligibleStock {
    symbole: string;
    nom_societe: string;
    secteur?: string;
    cours_actuel: number;
    score: number;
    stoch_k: number;
    stoch_d: number;
    rsi: number;
    avg_turnover?: number;
    is_liquid: boolean;
    is_opportunity: boolean;
    is_owned: boolean;
    analysis: string;
    recommendation?: string;
    potential_pct?: number;
    is_undervalued?: boolean;
    // OBV Field
    obv_trend?: string;
    // Golden Cross Fields (New)
    is_golden_cross?: boolean;
    sma_50?: number;
    sma_200?: number;
}

interface PrudentInvestorData {
    generated_at: string;
    portfolio_summary: PortfolioSummary;
    opportunities: SwingOpportunity[];
    sell_signals: SellSignal[];
    eligible_universe: EligibleStock[];
    market_sentiment: string;
}

export const usePrudentInvestor = () => {
    const { token } = useAuth();
    const [data, setData] = useState<PrudentInvestorData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/portfolio/swing-trader/prudent`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors du chargement');
            }

            const jsonData = await response.json();
            setData(jsonData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur réseau');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, refetch: fetchData };
};
